import React, { useState } from 'react';
import { Plus, Sparkles } from 'lucide-react';
import { LEXI_ENABLED } from '../lib/features';

const CATEGORIES = [
  {
    id: 'start',
    label: 'Before you start',
    items: [
      {
        q: 'Which path should I start with?',
        a: "If you haven't picked a stream yet, start with Path 1. If you've picked one but don't know what's inside it, start with Path 2. If you already have a direction and want to see the jobs it leads to, start with Path 4. Everything else opens up from there.",
      },
      {
        q: 'Can I be on more than one path?',
        a: "Yes — most people are on two or three. These aren't tracks you get locked into. They're rooms you can walk between.",
      },
      {
        q: 'Why not just ask ChatGPT, Gemini or any other AI about my career?',
        withoutLexi: 'LearnixTa organizes pathways, subjects and career-exploration material in one place. AI can make mistakes. Compare sources and discuss major decisions with people who understand your situation.',
        a: 'LearnixTa organizes pathways, subjects and career-exploration material in one place. Lexi is intended to use approved current LearnixTa sources, not just general model memory. AI can make mistakes here too. Compare sources and discuss major decisions with people who understand your situation.',
      },
      {
        q: 'Is this free?',
        withoutLexi: 'All 15 public pathways, lessons and quizzes are free without an account. Public learning remains open without login or payment.',
        a: 'All 15 public pathways, lessons and quizzes are free without an account. Lexi’s planned allowance is three total completed answers per recognised browser across the shared six-month launch—not three per day. Genuine chat responses are temporarily unavailable while model access and spending safeguards are verified. Public learning remains open without login or payment.',
      },
      {
        q: 'Do I need an account?',
        withoutLexi: 'No account is needed for public learning. Existing private accounts remain protected.',
        a: 'No. Public learning and the limited guest Lexi flow do not require an account. Guest chat remembers a recognised browser using a first-party cookie and server records, not a permanent physical-device identity. Existing private accounts remain protected and separate from guest chat. Public sign-in options are temporarily hidden.',
      },
    ],
  },
  {
    id: 'stage1',
    label: 'Stage 1 · Deciding what to pick',
    items: [
      {
        q: '1. "What do I take after 10th or Inter?" — Career Clarity',
        a: "The biggest decision of your school life, made with information instead of relatives' opinions. Clarity quizzes, stream-by-stream breakdowns — MPC, BiPC, Commerce, Humanities and beyond — and a straight preview of where each one actually leads. You leave knowing why you chose, not just what. Like any guide, LearnixTa can make mistakes — treat quiz results as direction, reverify with your teachers and family, and make the final call yourself.",
      },
      {
        q: '2. "Which specialization inside my degree?" — Inner Specializations',
        a: 'Picking B.Tech or a degree is only half the decision. This path opens the specializations inside it — CSE, AI-ML, Data Science, and the dozens nobody mentions — with what you\u2019ll actually study, what the work feels like day to day, and which ones fit you rather than just sound current. Syllabi differ by college and LearnixTa can make mistakes — always reverify on the official college or university website before you decide.',
      },
      {
        q: '3. "Will AI take this job?" — AI Risk Check',
        a: 'Before you spend years preparing for a career, check what\u2019s coming for it. This path breaks roles down task by task: what AI is likely to automate, what stays firmly human, and where to put your effort so it compounds instead of expires. These are predictions, not guarantees — LearnixTa can make mistakes, and nobody can perfectly forecast AI. Use this to think smarter, reverify from outside sources, and follow your own plan.',
      },
    ],
  },
  {
    id: 'stage2',
    label: 'Stage 2 · Exploring roles & directions',
    items: [
      {
        q: '4. "What jobs actually exist?" — Future-Ready Job Roles',
        a: 'A library of the roles that are genuinely out there — not just "software engineer" but the full map behind it. Each role comes with a day in the life, the tools you\u2019d use, the skills you\u2019d need, and a reality check on the parts nobody advertises. The market shifts constantly and LearnixTa can make mistakes — reverify current openings and requirements on official job portals too.',
      },
      {
        q: '5. "Can I get into AI?" — AI Pathway',
        a: 'AI is the fastest-growing career direction in India, and this path takes you inside it: the roles that exist today, the skills behind each one, and how to enter whether you come from a coding background or not. No "become an AI engineer in 30 days" — just the route in. This field changes every few months and LearnixTa can make mistakes — keep reverifying the latest skills and tools from official sources.',
      },
      {
        q: '6. "Is a bank job right for me?" — Banking Careers',
        a: 'Government and private banking, decoded — clerk, PO, and the specialist roles most people never hear about. What each exam demands, what the work looks like behind the counter and past it, and whether the stable-bank-job dream matches how you actually want to spend your days. Exam patterns change and LearnixTa can make mistakes — always reconfirm dates, eligibility and syllabus on the official IBPS/SBI/RBI websites.',
      },
      {
        q: '7. "I\u2019d rather work with my hands" — Blue-Collar Jobs (Skilled Trades)',
        a: 'The careers India talks about least and needs most: technicians, operators, machinists, field engineers. For people who\u2019d rather work on live systems than sit in a lecture hall — with a clear look at the pay, the progression, and where each trade leads in five years. Demand and training vary by city, and LearnixTa can make mistakes — reverify with nearby ITIs and training centres before enrolling anywhere.',
      },
      {
        q: '8. "I want to build my own thing" — OwnPath',
        a: 'For students who\u2019d rather build than apply. An all-India directory covering every state and union territory: government schemes, grants, loans and startup support — each one checked and date-stamped before we show an Apply button. If we couldn\u2019t verify it, we say so instead of sending you into a dead end. Even then, scheme rules and deadlines change without notice and LearnixTa can make mistakes — always reconfirm on the official government portal before applying.',
      },
    ],
  },
  {
    id: 'stage3',
    label: 'Stage 3 · Building proof',
    items: [
      {
        q: '9. "How do I prove I can do it?" — Build Badges',
        a: 'Reading about a career proves nothing; building something does. Build Badges gives you small, self-contained projects, grades your submission with AI feedback, and awards a badge you can show. It turns "I\u2019m interested in this" into "here\u2019s what I made." AI grading is smart but it can make mistakes — treat the feedback as a mentor\u2019s opinion, get a second view from a real person, and keep improving beyond the badge.',
      },
      {
        q: '10. "What about the skills nobody teaches?" — Life Skills',
        a: 'Communication, money sense, decision-making, handling failure — the things every career demands and no classroom covers. Free for everyone, because these shouldn\u2019t sit behind a paywall. Even here, LearnixTa can make mistakes — especially on money matters, reverify anything important with qualified people before acting on it.',
      },
      {
        q: '11. "How do I get an internship?" — Internship Pathfinder',
        a: 'The bridge from classroom to career. How internships actually work in India, where the genuine listings are, how to apply without a polished resume, and how to turn eight weeks of work into something that shows up on your CV. LearnixTa can make mistakes — independently verify every internship offer before sharing documents, and remember: genuine internships never ask you for money.',
      },
    ],
  },
  {
    id: 'stage4',
    label: 'Stage 4 · Entering & growing',
    items: [
      {
        q: '12. "I\u2019m starting my career" — Fresher Launchpad',
        a: 'For final-year students and freshers facing a first job hunt: resumes, interviews, reading an offer properly, and surviving the first ninety days. You enter the market prepared instead of panicked. Hiring trends shift every season and LearnixTa can make mistakes — use this as your foundation and reverify from official company pages too.',
      },
      {
        q: '13. "I\u2019m switching or growing" — Career Switch & Growth',
        a: 'For working professionals who feel stuck or want more — switching fields, upskilling with a plan, and growing where you already are. It answers the question lakhs of people are quietly asking: is it too late to change? Usually not — but it takes a plan, not a leap. Your situation has factors only you know, and LearnixTa can make mistakes — treat our guidance as direction, reverify outside, and build the final plan around your own life.',
      },
      {
        hidden: true, // 14th point kept for the government pitch — set hidden: false to show again
        q: '14. "If the government approves this plan" — Pre-Career Clarity Course',
        a: [
          'Our proposal to the government: a structured, trilingual (Telugu · Hindi · English), 24-week Pre-Career Clarity Course (PCC) for students from Class 9 to final-year degree — delivered digitally through each school\u2019s and college\u2019s own portal, powered by LearnixTa\u2019s 13 verified pathways — so no student in the state chooses a stream, degree, or career blindly again.',
          'STAGE 1 — Know Yourself (Weeks 1\u20134): Students take our Career Clarity tests and receive a documented Decision Report, shared with the student AND parent. Every student gets a data-backed starting point from day one.',
          'STAGE 2 — Know the Real World of Work (Weeks 5\u201312): Honest day-in-the-life previews of real roles across all four directions equally — corporate jobs, government/banking, skilled trades, and technology — ending the "IT-or-nothing" mindset. Inner Specializations prevent the classic disaster: right degree, wrong branch.',
          'STAGE 3 — Know the Future (Weeks 13\u201316): Every shortlisted role runs through our AI Risk Check (2030 Edition) — what AI will automate, what stays human, the student\u2019s human edge, and early-warning signals. No textbook, coaching centre, or other state program has this.',
          'STAGE 4 — Build Two Backups (Weeks 17\u201322): Every student, regardless of stream, completes one skilled-trade exposure module and one entrepreneurship module — a matched business idea plus one real verified government scheme with its official apply route — and earns one AI-graded Build Badge proof project. A student with three futures never fears one closing.',
          'STAGE 5 — Decide and Launch (Weeks 23\u201324): A final guided decision session plus free Life Skills fundamentals. Every student exits with a primary path, a backup trade, a backup venture idea, and a proof badge.',
          'DELIVERY: LearnixTa is the parent platform; every institution gets a portal in its own name; content updates flow automatically from the centre to every student instantly. PILOT: 10 institutions, one district, one academic term — at near-zero cost, since the platform already exists and Life Skills is free forever. MEASURE: Decision Reports issued, stage completion, backup paths identified, parent engagement. SCALE: pilot data \u2192 district \u2192 state. Andhra and Telangana first; other states follow what works.',
          'THE ASK: "Every year lakhs of our students choose streams and degrees blindly, and AI is now disrupting the one sector we push them all toward. We have built a working, trilingual, 24-week Pre-Career Clarity Course from 13 live career pathways — including India\u2019s only student-facing AI Risk Check and a verified directory of the government\u2019s own schemes. It costs almost nothing to pilot, gives every student a documented decision report their parents can read, and guarantees each child leaves with a primary path plus two backups. We ask for one district, one term, ten institutions. The data will speak after that."',
        ],
      },
    ],
  },
];

const HomeFAQSection = () => {
  const [activeCat, setActiveCat] = useState('start');
  const [openIdx, setOpenIdx] = useState(0);
  const category = CATEGORIES.find((c) => c.id === activeCat);
  const visibleItems = category.items.filter((i) => !i.hidden);

  const switchCat = (id) => {
    setActiveCat(id);
    setOpenIdx(0);
  };

  return (
    <section className="bg-gray-50 border-t border-gray-200 py-16 md:py-24 px-4 md:px-6" data-testid="home-faq-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1">
            <Sparkles size={11} /> The 14 Paths
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mt-4" data-testid="home-faq-heading">
            Frequently Asked Questions
          </h2>
          <p className="text-sm md:text-base text-gray-600 italic mt-3 max-w-xl">
            Grouped into four stages so you can find your entry point instead of scanning thirteen options.
          </p>
        </div>

        <div className="md:grid md:grid-cols-[260px_1fr] md:gap-10 lg:gap-14">
          {/* Category nav — pills on mobile, vertical list on desktop */}
          <div className="mb-6 md:mb-0">
            <div
              className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-none"
              data-testid="home-faq-category-nav"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => switchCat(cat.id)}
                  data-testid={`home-faq-cat-${cat.id}`}
                  className={`shrink-0 md:w-full text-left text-sm font-semibold rounded-full md:rounded-xl px-4 py-2 md:py-2.5 transition-[background-color,color,border-color] border whitespace-nowrap md:whitespace-normal ${
                    activeCat === cat.id
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400 hover:text-teal-700'
                  }`}
                >
                  {cat.label}
                  <span className={`ml-2 text-[11px] font-bold ${activeCat === cat.id ? 'text-teal-300' : 'text-gray-400'}`}>
                    {cat.items.filter((i) => !i.hidden).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-3" data-testid="home-faq-list">
            {visibleItems.map((item, idx) => {
              const open = openIdx === idx;
              const answer = !LEXI_ENABLED && item.withoutLexi ? item.withoutLexi : item.a;
              return (
                <div
                  key={item.q}
                  className={`bg-white border rounded-2xl transition-[border-color,box-shadow] ${
                    open ? 'border-teal-300 shadow-md shadow-teal-100/60' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : idx)}
                    className="w-full flex items-start justify-between gap-4 text-left px-5 py-4 md:px-6 md:py-5"
                    data-testid={`home-faq-q-${activeCat}-${idx}`}
                    aria-expanded={open}
                  >
                    <span className="text-[15px] md:text-base font-semibold text-gray-900 leading-snug">
                      {item.q}
                    </span>
                    <span
                      className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-300 ${
                        open ? 'bg-teal-600 text-white rotate-45' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <Plus size={14} />
                    </span>
                  </button>
                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-out"
                    style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 md:px-6 pb-5 space-y-3" data-testid={`home-faq-a-${activeCat}-${idx}`}>
                        {(Array.isArray(answer) ? answer : [answer]).map((para, k) => (
                          <p key={k} className="text-sm md:text-[15px] leading-relaxed text-gray-600">{para}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Honesty footer — shown once below everything */}
            <div className="mt-6 bg-gray-900 rounded-2xl px-5 py-6 md:px-8 md:py-7" data-testid="home-faq-disclaimer">
              <p className="text-white font-bold text-base md:text-lg">LearnixTa can make mistakes.</p>
              <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                Everything here is researched guidance and prediction — not a guarantee. Reverify important
                details from official sources, and make final decisions with your own plan, your family and
                your mentors.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeFAQSection;
